import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    Box, Button, Grid, Dialog, DialogContent, Typography, OutlinedInput, FormControl, FormGroup, FormLabel, Select, MenuItem
} from '@material-ui/core'
import ProviderAPI from 'config/api/ProviderAPI'
import { onError } from 'config/lib/errorLib'
import { NotificationManager } from 'react-notifications'
import Loader from 'components/general/Loader'
import CountySelect from 'components/general/CountySelect'
import ReactDropzoneCropper from 'components/general/ReactDropzoneCropper'
import config from 'config/api/Config'
import { Storage } from 'aws-amplify'
import CreditCardIcon from '@material-ui/icons/CreditCard'
import ProjectType from 'components/provider/ProjectType'
import { getVideoEmbedUrl } from 'config/helpers/Helpers'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { cleanQuillHtml } from 'config/helpers/Helpers'

function stripHtml(html) {
    html = html || '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
}

function EditProjectCard({ open, onClose, project, company, updateCompany }) {
    const api = new ProviderAPI()
    const [form, setForm] = useState({ ...project })
    const [image, setImage] = useState(null)
    const [imagePreview, setImagePreview] = useState(project.image || '')
    const [showVideoUrl, setShowVideoUrl] = useState(false)
    const [loading, setLoading] = useState(false)

    // Structured fields state
    const [structured, setStructured] = useState({
        panels: project.panels || '',
        costAfterGrant: project.costAfterGrant || '',
        annualSavings: project.annualSavings || '',
    });

    // Helper: auto-calculate payback period when cost and savings are provided.
    const paybackPeriod = (structured.costAfterGrant && structured.annualSavings && Number(structured.annualSavings) > 0)
        ? (Number(structured.costAfterGrant) / Number(structured.annualSavings)).toFixed(1)
        : '';

    function handleChange(field, value) {
        setForm(prev => ({ ...prev, [field]: value }))
        if (field === 'image') {
            setImagePreview(value)
            setImage(null)
        }
    }

    async function handleImage(file) {
        const preview = URL.createObjectURL(file)
        setImage(file)
        setImagePreview(preview)
        setForm(prev => ({ ...prev, image: '' }))
        setShowVideoUrl(false)
    }

    function openVideoUrl() {
        setShowVideoUrl(true)
        setImage(null)
        setImagePreview('')
    }

    function checkFields() {
        // Only the Residential/Commercial category is required; everything else is optional.
        return !form.category
    }

    async function saveProject() {
        setLoading(true)
        try {
            let imageUrl = imagePreview

            if (image) {
                const path = `projects/${project.projectId}`
                await Storage.put(path, image, {
                    contentType: image.type,
                    level: 'public'
                })
                imageUrl = `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/${path}`
            }

            const updatedProjects = company.projects.map(p =>
                p.projectId === project.projectId
                    ? {
                        ...p,
                        type: form.type,
                        category: form.category,
                        county: form.county,
                        description: cleanQuillHtml(form.description),
                        image: imageUrl,
                        // Structured fields (each optional and independent)
                        panels: structured.panels || undefined,
                        costAfterGrant: structured.costAfterGrant || undefined,
                        annualSavings: structured.annualSavings || undefined,
                        paybackPeriod: paybackPeriod || undefined
                    } : p
            )

            const updatedCompany = await api.updateProjects(company.companyId, updatedProjects)
            updateCompany(updatedCompany)
            NotificationManager.success('Case Study updated')
            onClose()
        } catch (e) {
            onError(e)
        } finally {
            setLoading(false)
        }
    }

    const quillModules = {
        toolbar: [
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['bold', 'italic'],
        ]
    }
    const quillFormats = [
        'list', 'bullet', 'bold', 'italic'
    ]

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogContent>
                <Grid container>
                    <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                        <Box component={Typography} variant='h3'>
                            <Box component='span'>Edit Case Study</Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>Case Study Type</FormLabel>
                            <Grid container>
                                <Grid item xs={6}>
                                    <ProjectType
                                        title='Image Case Study'
                                        icon={<CreditCardIcon style={{ height: 30, width: 30, color: '#229626' }} />}
                                        selectedMethod={form.type}
                                        onClick={() => handleChange('type', 'Image')}
                                        active={form.type === 'Image'}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <ProjectType
                                        title='Video Case Study'
                                        icon={<CreditCardIcon style={{ height: 30, width: 30, color: '#229626' }} />}
                                        selectedMethod={form.type}
                                        onClick={() => handleChange('type', 'Video')}
                                        active={form.type === 'Video'}
                                    />
                                </Grid>
                            </Grid>
                        </FormGroup>
                    </Grid>

                    {form.type && (
                        <>
                            <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                                <FormGroup>
                                    {showVideoUrl && (
                                        <FormControl variant='filled' component={Box} width='100%' style={{ marginBottom: '1rem' }}>
                                            <OutlinedInput
                                                autoComplete='off'
                                                type='text'
                                                placeholder='www.youtube.com/my-video'
                                                value={form.image || ''}
                                                onChange={(e) => {
                                                    handleChange('image', e.target.value)
                                                    setImagePreview(e.target.value)
                                                }}
                                            />
                                        </FormControl>
                                    )}

                                    <FormControl
                                        variant='filled'
                                        component={Box}
                                        width='100%'
                                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                    >
                                        {/* Image preview */}
                                        {form.type === 'Image' && imagePreview && (
                                            <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' width='100%'>
                                                <img
                                                    className='case-study-preview'
                                                    src={imagePreview}
                                                    alt='Case Study Image'
                                                    style={{ marginBottom: '1rem', borderRadius: '4px', textAlign: 'center' }}
                                                />
                                            </Box>
                                        )}

                                        {/* Video preview */}
                                        {form.type === 'Video' && imagePreview && (
                                            <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' width='100%'>
                                                {(typeof imagePreview === 'string' && (
                                                    imagePreview.includes('youtube.com') ||
                                                    imagePreview.includes('youtu.be') ||
                                                    imagePreview.includes('vimeo.com')
                                                )) ? (
                                                    <iframe
                                                        width='80%'
                                                        height='360'
                                                        src={getVideoEmbedUrl(imagePreview)}
                                                        frameBorder='0'
                                                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                                                        allowFullScreen
                                                        title='Case Study Video'
                                                        style={{ marginBottom: '1rem', borderRadius: '4px' }}
                                                    />
                                                ) : (
                                                    <video
                                                        className='case-study-preview'
                                                        controls
                                                        style={{ marginBottom: '1rem', borderRadius: '4px', textAlign: 'center' }}
                                                    >
                                                        <source src={imagePreview} type='video/mp4' />
                                                        Your browser does not support the video tag.
                                                    </video>
                                                )}
                                            </Box>
                                        )}

                                        {/* Show upload area as box if no image/video is present */}
                                        {(!imagePreview || imagePreview === '') && (
                                            <ReactDropzoneCropper
                                                updateImage={handleImage}
                                                companyId={company.companyId}
                                                noPreview
                                                uploadText={`Upload New ${form.type}`}
                                                type={form.type}
                                                displayStyle='box'
                                            />
                                        )}
                                        {/* Otherwise, show as button for replacement */}
                                        {(imagePreview && imagePreview !== '') && (
                                            <ReactDropzoneCropper
                                                updateImage={handleImage}
                                                companyId={company.companyId}
                                                noPreview
                                                uploadText={`Replace ${form.type}`}
                                                type={form.type}
                                                displayStyle='button'
                                            />
                                        )}

                                        {form.type === 'Video' && (
                                            <Button
                                                variant='contained'
                                                color='secondary'
                                                style={{ marginTop: '1rem' }}
                                                onClick={() => openVideoUrl()}
                                            >
                                                Link Video (YouTube/Vimeo)
                                            </Button>
                                        )}
                                    </FormControl>
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormGroup>
                                    <FormLabel>Case Study Category</FormLabel>
                                    <FormControl variant='filled' component={Box} width='100%'>
                                        <Select
                                            value={form.category || ''}
                                            onChange={(e) => handleChange('category', e.target.value)}
                                            input={<OutlinedInput />}
                                            style={{ marginBottom: '0.5rem' }}
                                        >
                                            <MenuItem value='Residential'>Residential</MenuItem>
                                            <MenuItem value='Commercial'>Commercial</MenuItem>
                                        </Select>
                                    </FormControl>
                                </FormGroup>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormGroup>
                                    <FormLabel>Case Study County (optional)</FormLabel>
                                    <FormControl variant='filled' component={Box} width='100%'>
                                        <CountySelect
                                            counties={form.county || ''}
                                            updateCounties={(county) => handleChange('county', county)}
                                            allOption={false}
                                            multiple={false}
                                        />
                                    </FormControl>
                                </FormGroup>
                            </Grid>

                            <Grid item xs={12}>
                                <FormGroup>
                                    <FormLabel>Case Study Description</FormLabel>
                                    <FormControl variant='filled' component={Box} width='100%'>
                                        <ReactQuill
                                            theme="snow"
                                            value={form.description || ''}
                                            onChange={value => handleChange('description', value)}
                                            modules={quillModules}
                                            formats={quillFormats}
                                            style={{ background: '#fff' }}
                                        />
                                    </FormControl>
                                </FormGroup>
                            </Grid>
                            {/* Structured fields (optional, all-or-none) */}
                            <Grid item xs={12} style={{ marginBottom: 16 }}>
                                <FormGroup>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Box component={Typography} variant='h3'>
                                                <Box component='span'>Additional Details (Recommended)</Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormGroup>
                                                <FormLabel>How many panels?</FormLabel>
                                                <FormControl
                                                    variant='filled'
                                                    component={Box}
                                                    width='100%'
                                                >
                                                    <OutlinedInput
                                                        autoComplete='off'
                                                        type='number'
                                                        placeholder='e.g. 12'
                                                        value={structured.panels}
                                                        onChange={e => setStructured(s => ({ ...s, panels: e.target.value }))}
                                                        inputProps={{ min: 0 }}
                                                    />
                                                </FormControl>
                                            </FormGroup>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormGroup>
                                                <FormLabel>Cost after grant (€)</FormLabel>
                                                <FormControl
                                                    variant='filled'
                                                    component={Box}
                                                    width='100%'
                                                >
                                                    <OutlinedInput
                                                        autoComplete='off'
                                                        type='number'
                                                        placeholder='e.g. 6500'
                                                        value={structured.costAfterGrant}
                                                        onChange={e => setStructured(s => ({ ...s, costAfterGrant: e.target.value }))}
                                                        inputProps={{ min: 0 }}
                                                    />
                                                </FormControl>
                                            </FormGroup>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormGroup>
                                                <FormLabel>Estimated annual electricity savings (€)</FormLabel>
                                                <FormControl
                                                    variant='filled'
                                                    component={Box}
                                                    width='100%'
                                                >
                                                    <OutlinedInput
                                                        autoComplete='off'
                                                        type='number'
                                                        placeholder='e.g. 1200'
                                                        value={structured.annualSavings}
                                                        onChange={e => setStructured(s => ({ ...s, annualSavings: e.target.value }))}
                                                        inputProps={{ min: 0 }}
                                                    />
                                                </FormControl>
                                            </FormGroup>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormGroup>
                                                <FormLabel>Payback period (years)</FormLabel>
                                                <FormControl
                                                    variant='filled'
                                                    component={Box}
                                                    width='100%'
                                                >
                                                    <OutlinedInput
                                                        autoComplete='off'
                                                        type='number'
                                                        placeholder='Auto-calculated'
                                                        value={paybackPeriod}
                                                        readOnly
                                                    />
                                                </FormControl>
                                            </FormGroup>
                                        </Grid>
                                    </Grid>
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display='flex' justifyContent='space-between' width='100%'>
                                    <Button onClick={onClose} color="primary" variant="outlined">
                                        Cancel
                                    </Button>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        onClick={saveProject}
                                        disabled={checkFields() || loading}
                                    >
                                        {!loading ? 'Save' : <Loader height={12} />}
                                    </Button>
                                </Box>
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

export default EditProjectCard